import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from '@tanstack/react-router';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { DatosDelTercero } from '../componentes/DatosDelTercero';
import { FichaDeSeguimiento } from '../componentes/FichaDeSeguimiento';
import { vigenteHoy, type ConVigencia } from '../lib/fecha';
import { importeParaMirar } from '../lib/numero';
import type { Tercero } from './Proveedores';

/**
 * Los importes se guardan con cuatro decimales; en pantalla sobran dos. Un límite de «500000.0000»
 * se lee peor que «500000.00» y no dice nada más —y acá encima es un campo que alguien va a
 * reescribir—.
 */
function sinCerosDeMas(importe: string): string {
  return /\.\d{2}00$/.test(importe) ? importe.slice(0, -2) : importe;
}

interface MovimientoDeCuenta {
  readonly id: string;
  readonly tipo: string;
  readonly importe: string;
  readonly vencimiento: string | null;
  readonly concepto: string | null;
  readonly ocurridoEn: string;
}

interface CuentaDelTercero {
  readonly saldo: string;
  readonly movimientos: readonly MovimientoDeCuenta[];
}

interface ComprobanteDelCliente {
  readonly id: string;
  readonly tipo: string;
  readonly numero: string;
  readonly emitidoEn: string;
  readonly total: string;
  readonly estado: string;
}

interface QueSeLleva {
  readonly articuloId: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly cantidad: string;
  readonly importe: string;
  readonly ultimaVez: string;
}

/**
 * La ficha de un cliente: quién es y qué pasó con él.
 *
 * ## Por qué es una pantalla propia y no la misma del proveedor
 *
 * Lo decidió el dueño del producto. Un tercero puede ser las dos cosas —el corralón al que se le
 * compra cemento y se le vende pintura— y lo que **es del tercero** vive una sola vez: el
 * formulario de datos es un componente compartido. Lo que cambia entre las dos fichas es el perfil
 * y lo que se muestra abajo, que no tienen nada que ver: acá cuentas por cobrar y comprobantes
 * emitidos, allá órdenes y facturas de compra.
 *
 * ## El saldo que se muestra es el del plano cliente
 *
 * Y no la resta de los dos. Es la regla de E5 y no es un detalle de presentación: lo que un tercero
 * me debe como cliente y lo que le debo como proveedor **son dos cuentas que no se compensan**.
 */
export function FichaDeCliente(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { id } = useParams({ from: '/clientes/$id' });
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeEditar = usePermiso('TERCEROS.TERCERO.EDITAR');
  const puedeEditarPerfil = usePermiso('TERCEROS.CLIENTE.EDITAR');
  const puedeVerCuentas = usePermiso('CUENTAS.CUENTA.VER');
  const puedeVerVentas = usePermiso('VENTAS.COMPROBANTE.VER');

  const [seguimiento, setSeguimiento] = useState(false);
  const [limite, setLimite] = useState<string | null>(null);
  // La lista elegida en la ficha; `null` es «no la tocaron» (E36).
  const [lista, setLista] = useState<string | null>(null);
  const puedeVerListas = usePermiso('PRECIOS.LISTA.VER');
  const listas = useQuery({
    queryKey: ['listas-de-precios'],
    enabled: puedeVerListas,
    queryFn: () =>
      pedir<({ id: string; nombre: string; esPredeterminada: boolean } & ConVigencia)[]>(
        '/listas-de-precios',
      ),
  });
  const [error, setError] = useState<string | null>(null);

  const tercero = useQuery({
    queryKey: ['tercero', id],
    queryFn: () => pedir<Tercero>(`/terceros/${id}`),
  });
  const cuenta = useQuery({
    queryKey: ['cuenta-del-tercero', id, 'cliente'],
    enabled: puedeVerCuentas,
    queryFn: () => pedir<CuentaDelTercero>(`/cuentas-corrientes/${id}?plano=cliente`),
  });
  const comprobantes = useQuery({
    queryKey: ['comprobantes-del-tercero', id],
    enabled: puedeVerVentas,
    queryFn: () => pedir<ComprobanteDelCliente[]>(`/comprobantes?terceroId=${id}`),
  });
  const queSeLleva = useQuery({
    queryKey: ['que-se-lleva', id],
    enabled: puedeVerVentas,
    queryFn: () => pedir<QueSeLleva[]>(`/informes/terceros/${id}/que-se-lleva`),
  });

  const guardarPerfil = useMutation({
    mutationFn: () =>
      pedir<void>(`/terceros/${id}/cliente`, {
        method: 'PUT',
        // Vacío es «sin tope» y no «cero»: son cosas distintas y el servidor las distingue.
        cuerpo: {
          limiteDeCredito: (limite ?? '').trim() === '' ? null : (limite ?? '').trim(),
          // Sólo si la cambiaron: lo que no se manda, el servidor no lo pisa.
          ...(lista === null ? {} : { listaDePreciosId: lista === '' ? null : lista }),
        },
      }),
    onSuccess: async () => {
      setError(null);
      setLimite(null);
      setLista(null);
      await cola.invalidateQueries({ queryKey: ['tercero', id] });
      await cola.invalidateQueries({ queryKey: ['terceros'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  if (tercero.isPending) return <Cargando />;
  if (tercero.isError) {
    return (
      <Problema
        error={tercero.error}
        reintentar={() => {
          void tercero.refetch();
        }}
      />
    );
  }
  const ficha = tercero.data;
  if (ficha === undefined) return <Vacio />;

  // Una lista asignada que hoy no sirve no rompe nada —la venta sigue con la siguiente de la
  // cadena—, pero el precio que se cobra deja de ser el que alguien eligió acá: se dice.
  const listaElegida = (listas.data ?? []).find(
    (una) => una.id === (lista ?? ficha.listaDePreciosDelCliente),
  );
  const listaQueNoSirve = listaElegida !== undefined && !vigenteHoy(listaElegida);

  return (
    <>
      <Encabezado
        titulo={ficha.razonSocial}
        descripcion={ficha.identificacion ?? ficha.nombreComercial ?? ''}
        acciones={
          <>
            <Insignia tono={ficha.activo ? 'bien' : 'neutro'}>
              {ficha.activo ? t('catalogo.activo') : t('catalogo.inactivo')}
            </Insignia>
            {ficha.esProveedor ? (
              // Es las dos cosas: se dice, y se enlaza. Verlo es lo que evita que alguien lo
              // cargue dos veces, y que se busque su deuda en la pantalla equivocada.
              <Link to="/proveedores/$id" params={{ id }}>
                <Boton tono="secundario">{t('ficha.tambienProveedor')}</Boton>
              </Link>
            ) : null}
            <Link to="/clientes">
              <Boton tono="secundario">{t('acciones.volver')}</Boton>
            </Link>
          </>
        }
      />

      <div className="flex flex-col gap-4">
        <DatosDelTercero
          tercero={ficha}
          puedeEditar={puedeEditar}
          alGuardar={['tercero', 'terceros']}
        />

        <div className="grid gap-4 lg:grid-cols-2">
          <Tarjeta
            titulo={t('ficha.perfilDeCliente')}
            descripcion={t('ficha.ayudaPerfilDeCliente')}
            acciones={
              puedeEditarPerfil ? (
                <Boton
                  cargando={guardarPerfil.isPending}
                  onClick={() => {
                    guardarPerfil.mutate();
                  }}
                >
                  {t('acciones.guardar')}
                </Boton>
              ) : undefined
            }
          >
            <div className="flex flex-col gap-2">
              <Campo
                etiqueta={t('clientes.limiteDeCredito')}
                inputMode="decimal"
                disabled={!puedeEditarPerfil}
                value={limite ?? sinCerosDeMas(ficha.limiteDeCredito ?? '')}
                onChange={(evento) => {
                  setLimite(evento.target.value);
                }}
              />
              <p className="text-apoyo text-texto-tenue">{t('ficha.limiteVacio')}</p>
              {puedeVerListas ? (
                <Selector
                  etiqueta={t('ficha.listaDePrecios')}
                  disabled={!puedeEditarPerfil}
                  value={lista ?? ficha.listaDePreciosDelCliente ?? ''}
                  ayuda={t('ficha.ayudaListaDePrecios')}
                  onChange={(evento) => {
                    setLista(evento.target.value);
                  }}
                >
                  <option value="">{t('ficha.sinListaPropia')}</option>
                  {(listas.data ?? []).map((una) => (
                    <option key={una.id} value={una.id}>
                      {una.nombre}
                    </option>
                  ))}
                </Selector>
              ) : null}
              {listaQueNoSirve ? (
                <p role="status" className="text-apoyo text-atencion">
                  {t('ficha.listaQueNoSirve')}
                </p>
              ) : null}
              {error === null ? null : (
                <p role="alert" className="text-xs text-mal">
                  {error}
                </p>
              )}
            </div>
          </Tarjeta>

          {puedeVerCuentas ? (
            <Tarjeta
              titulo={t('ficha.cuentaCorriente')}
              descripcion={t('ficha.ayudaCuentaCliente')}
              acciones={
                <Link to="/estado-de-cuenta/$terceroId" params={{ terceroId: id }}>
                  <Boton tono="secundario">{t('cuentas.estadoDeCuenta')}</Boton>
                </Link>
              }
            >
              {cuenta.isPending ? <Cargando /> : null}
              {cuenta.isSuccess ? (
                <div className="flex flex-col gap-2">
                  <p className="text-titulo font-semibold numero">
                    {importeParaMirar(cuenta.data.saldo, i18n.language)}
                  </p>
                  <Tabla
                    columnas={[
                      {
                        clave: 'cuando',
                        titulo: t('existencias.cuando'),
                        contenido: (fila: MovimientoDeCuenta) =>
                          new Date(fila.ocurridoEn).toLocaleDateString(),
                      },
                      {
                        clave: 'concepto',
                        titulo: t('cuentas.concepto'),
                        contenido: (fila: MovimientoDeCuenta) => fila.concepto ?? fila.tipo,
                      },
                      {
                        clave: 'importe',
                        titulo: t('ficha.importe'),
                        numerica: true,
                        contenido: (fila: MovimientoDeCuenta) =>
                          importeParaMirar(fila.importe, i18n.language),
                      },
                    ]}
                    filas={cuenta.data.movimientos.slice(0, 10)}
                    claveDeFila={(fila) => fila.id}
                    vacio={<Vacio titulo={t('ficha.sinCuenta')} />}
                  />
                </div>
              ) : null}
            </Tarjeta>
          ) : null}
        </div>

        {puedeVerVentas ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Tarjeta titulo={t('ficha.comprobantes')} descripcion={t('ficha.ayudaComprobantes')}>
              {comprobantes.isPending ? <Cargando /> : null}
              {comprobantes.isSuccess ? (
                <Tabla
                  columnas={[
                    {
                      clave: 'cuando',
                      titulo: t('existencias.cuando'),
                      contenido: (fila: ComprobanteDelCliente) =>
                        new Date(fila.emitidoEn).toLocaleDateString(),
                    },
                    {
                      clave: 'numero',
                      titulo: t('compras.numero'),
                      contenido: (fila: ComprobanteDelCliente) => fila.numero,
                    },
                    {
                      clave: 'estado',
                      titulo: t('catalogo.estado'),
                      contenido: (fila: ComprobanteDelCliente) => (
                        <Insignia tono={fila.estado === 'emitido' ? 'bien' : 'neutro'}>
                          {fila.estado}
                        </Insignia>
                      ),
                    },
                    {
                      clave: 'total',
                      titulo: t('venta.total'),
                      numerica: true,
                      contenido: (fila: ComprobanteDelCliente) =>
                        importeParaMirar(fila.total, i18n.language),
                    },
                  ]}
                  filas={comprobantes.data}
                  claveDeFila={(fila) => fila.id}
                  vacio={<Vacio titulo={t('ficha.sinComprobantes')} />}
                />
              ) : null}
            </Tarjeta>

            <Tarjeta titulo={t('ficha.queSeLleva')} descripcion={t('ficha.ayudaQueSeLleva')}>
              {queSeLleva.isPending ? <Cargando /> : null}
              {queSeLleva.isSuccess ? (
                <Tabla
                  columnas={[
                    {
                      clave: 'articulo',
                      titulo: t('precios.articulo'),
                      contenido: (fila: QueSeLleva) => (
                        <Link
                          to="/articulos/$id"
                          params={{ id: fila.articuloId }}
                          className="text-acento underline"
                        >
                          {fila.nombre}
                        </Link>
                      ),
                    },
                    {
                      clave: 'cantidad',
                      titulo: t('existencias.cantidad'),
                      numerica: true,
                      contenido: (fila: QueSeLleva) => fila.cantidad,
                    },
                    {
                      clave: 'importe',
                      titulo: t('ficha.importe'),
                      numerica: true,
                      contenido: (fila: QueSeLleva) =>
                        importeParaMirar(fila.importe, i18n.language),
                    },
                    {
                      clave: 'ultima',
                      titulo: t('ficha.ultimaVez'),
                      contenido: (fila: QueSeLleva) => fila.ultimaVez,
                    },
                  ]}
                  filas={queSeLleva.data}
                  claveDeFila={(fila) => fila.articuloId}
                  vacio={<Vacio titulo={t('ficha.sinVentas')} />}
                />
              ) : null}
            </Tarjeta>
          </div>
        ) : null}

        <Tarjeta
          titulo={t('seguimiento.titulo')}
          descripcion={t('ficha.ayudaSeguimiento')}
          acciones={
            <Boton
              onClick={() => {
                setSeguimiento(true);
              }}
            >
              {t('seguimiento.titulo')}
            </Boton>
          }
        >
          <p className="text-apoyo text-texto-tenue">{t('ficha.seguimientoAparte')}</p>
        </Tarjeta>
      </div>

      <FichaDeSeguimiento
        terceroId={id}
        nombre={ficha.razonSocial}
        abierto={seguimiento}
        onCerrar={() => {
          setSeguimiento(false);
        }}
      />
    </>
  );
}
